import React from 'react';
import clsx from 'clsx';
import SvgRingilAvatar from '@icons/ringil-avatar.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AssistantChatMessage } from '@hooks/useAssistantChat';
import AssistantCitationCard from './AssistantCitationCard';
import AssistantMarkdown from './AssistantMarkdown';
import AssistantSuggestions from './AssistantSuggestions';
import { AssistantDivider, AssistantSectionTitle } from './AssistantSection';
import { phasesOf } from './assistantSteps';
import SvgCheckCircleSolid from '@icons/check-circle-solid.svg?react';
import { assistantMessageTime } from './assistantDate';

interface ComponentProps {
  message: AssistantChatMessage;
  /** Display name + initials of the logged user, shown on their own bubbles. */
  userName: string;
  userInitials: string;
  onRetry: (text: string) => void;
  /** Sends a clarification choice as the next question. */
  onPickSuggestion: (text: string) => void;
  /** Whether this message's clarification choices can still be picked (the latest answer only). */
  suggestionsActive: boolean;
  /** What the user asked next, to mark the choice they picked. */
  nextQuestion?: string;
}

/** Keep in step with the `assistantTyping` animation duration in tailwind.config.js. */
const TYPING_CYCLE_MS = 1200;

/**
 * Three dots in a white pill while the answer is being generated (Figma 21749:2054). The brand colour
 * passes from dot to dot with a fading trail — each dot runs the same cycle a third of a turn later.
 */
const TypingIndicator: React.FC = () => (
  <div className="flex h-[27px] w-[55px] items-center justify-center gap-[5px] rounded-full bg-white">
    {[0, 2, 1].map((third, index) => (
      <span
        key={index}
        className="h-[7px] w-[7px] animate-assistantTyping rounded-full bg-content-quaternary motion-reduce:animate-none"
        style={{ animationDelay: `-${(third * TYPING_CYCLE_MS) / 3}ms` }}
      />
    ))}
  </div>
);

/**
 * What the assistant is doing while an answer is on its way, fed by the stream's pipeline steps.
 *
 * Before any text arrives, the phases so far are listed — the finished ones ticked, the current one
 * with the typing dots — so a slow answer shows its progress instead of dots alone. Once text streams
 * in it takes the space, and only the current phase stays, under it.
 */
const AnswerProgress: React.FC<{ steps: string[] | undefined; writing: boolean }> = ({ steps, writing }) => {
  const { tr } = useTranslationLgs();
  const phases = phasesOf(steps, writing);
  const current = phases[phases.length - 1];
  const done = writing ? [] : phases.slice(0, -1);

  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      {done.map(phase => (
        <div key={phase.id} className="flex items-center gap-2 text-xs text-content-secondary">
          <SvgCheckCircleSolid className="h-3.5 w-3.5 shrink-0 text-complementary-processing" />
          {tr(phase.labelKey, phase.labelDefault)}
        </div>
      ))}
      <div className="flex items-center gap-2">
        <TypingIndicator />
        <span className="text-xs text-content-primary">{tr(current.labelKey, current.labelDefault)}…</span>
      </div>
    </div>
  );
};

/**
 * One turn of the conversation (Figma 21746:18244). User turns are the light blue bubble inset from the
 * left; answers are the grey bubble inset from the right, made of the markdown body, then the choices
 * of a clarification, then the related guides — each section set off by a divider.
 */
const AssistantMessageBubble: React.FC<ComponentProps> = ({ message, userName, userInitials, onRetry, onPickSuggestion, suggestionsActive, nextQuestion }) => {
  const { tr } = useTranslationLgs();
  const isUser = message.role === 'USER';

  // SYSTEM_SUMMARY turns are the backend condensing older context — informational, not a real turn.
  if (message.role === 'SYSTEM_SUMMARY') {
    return <div className="px-6 py-1 text-center text-xs italic text-content-secondary">{message.content}</div>;
  }

  const pending = message.state === 'pending';
  const failed = message.state === 'failed';
  const citations = [...(message.citations ?? [])].sort((a, b) => a.rank - b.rank);
  const hasSuggestions = !isUser && !pending && !failed && message.suggestions?.length > 0;

  return (
    // The bubble is inset on the opposite side so user and assistant turns read as two columns.
    <div className={clsx('flex w-full flex-col', isUser ? 'pl-6' : 'pr-6')}>
      <div
        className={clsx(
          'flex w-full flex-col gap-2 rounded border border-solid p-4 shadow-[0_2px_8px_0_rgba(0,0,0,0.05),0_16px_48px_0_rgba(254,80,0,0.02)]',
          isUser && 'border-complementary-processingMedium bg-[rgba(178,233,246,0.3)]',
          !isUser && !failed && 'bg-base-tertiary',
          // Outlined only while it is still being written.
          !isUser && !failed && (pending ? 'border-content-tertiary' : 'border-transparent'),
          !isUser && failed && 'border-complementary-errorMedium bg-complementary-errorLight',
        )}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {isUser ? (
              <div className="flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full bg-complementary-processing text-xs font-bold leading-3 text-white">
                {userInitials}
              </div>
            ) : (
              <SvgRingilAvatar className="h-[21px] w-[21px] shrink-0" />
            )}
            <div className="truncate text-xs text-content-primary">{isUser ? userName : tr('Assistant.botName', 'Ringil Automat')}</div>
          </div>
          <div className="shrink-0 text-xs text-content-secondary">{assistantMessageTime(message.createdAt)}</div>
        </div>

        {pending ? (
          <div className="flex w-full flex-col gap-4">
            {message.content && <AssistantMarkdown content={message.content} interactive={false} />}
            <AnswerProgress steps={message.progress} writing={!!message.content} />
          </div>
        ) : isUser || failed ? (
          // The user's own text and error messages are plain — only answers carry markdown.
          <div className={clsx('w-full whitespace-pre-wrap break-words text-sm', failed ? 'text-complementary-error' : 'text-content-primary')}>{message.content}</div>
        ) : (
          <div className="flex w-full flex-col gap-4">
            <AssistantMarkdown content={message.content} />

            {hasSuggestions && <AssistantSuggestions suggestions={message.suggestions} chosen={nextQuestion} active={suggestionsActive} onPick={onPickSuggestion} />}

            {citations.length > 0 && (
              <>
                <AssistantDivider />
                <div className="flex w-full flex-col gap-2">
                  <AssistantSectionTitle>{tr('Assistant.relatedGuides', 'Související návody')}</AssistantSectionTitle>
                  {/* One card of joined rows: 1px gaps between them, only the outer corners rounded. */}
                  <div className="flex w-full flex-col gap-px">
                    {citations.map(citation => (
                      <AssistantCitationCard key={`${citation.rank}-${citation.doc_id}`} citation={citation} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {failed && message.retryText && (
          <button
            type="button"
            onClick={() => onRetry(message.retryText)}
            className="cursor-pointer self-start border-none bg-transparent p-0 text-xs tracking-[0.24px] text-content-brand underline"
          >
            {tr('Assistant.retry', 'Try again')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AssistantMessageBubble;
